import { DataType } from './DataType';
import { GlueIntegration } from './Integration';
import { FunctionDefinition, GlueVariable, NodeInstanceDataPort, NodeInstanceState } from './Graph';
import { DataValue } from './DataValue';
import { IntegrationExecutionAccess } from './IntegrationExecutionAccess';
import { createOptional } from './higherOrder/createOptional';

export interface DataPort {
    name: string;
    type: DataType;
    optional?: boolean;
}

export const inherentInPortName = 'InherentIn';

export const inherentInPortTemplate: NodeInstanceDataPort = {
    name: inherentInPortName,
    type: { kind: 'null' },
    optional: true,
};

export const inherentOutPortName = 'InherentOut';

export const inherentOutPortTemplate: NodeInstanceDataPort = {
    name: inherentOutPortName,
    type: { kind: 'null' },
    optional: true,
};

export interface BaseNode {
    id: string;
    name: string;
    description: string;
    aiDescription?: string;
    category: string;
    color: string;
    hasExecutionDecisions: boolean;
    findableInLibrary: boolean;
    isAsync: boolean;
    inPorts: DataPort[];
    outPorts: DataPort[];
    triggerType?: TriggerType;
    triggerEventIdentifier?: string;
    integration?: GlueIntegration<any>;
    interfaceComponentId?: string; // New: reference to frontend component
    /**
     * State keys that should be stripped when a node is copy-pasted.
     * Used for backend resource IDs (e.g. apiId, formId) that must be
     * recreated fresh for each node instance.
     */
    transientStateKeys?: string[];
    hideInherentInPort?: boolean;
    hideInherentOutPort?: boolean;
    /**
     * Current version of this node's schema. Paired with `NodeInstance.nodeVersion`
     * so migrations can upgrade stored instances to match this version.
     */
    version?: number;
}

export interface ComputeShapeInput {
    preResolvedInputPorts: NodeInstanceDataPort[];
    preResolvedOutputPorts: NodeInstanceDataPort[];
    nodeInPorts: DataPort[];
    nodeOutPorts: DataPort[];
    selectedVariable?: GlueVariable;
    setVariableType: (variableName: string, type?: DataType) => void;
    portValues: { [key: string]: DataValue };
    state?: NodeInstanceState;
    // Function context for function-related nodes
    referencedFunctionId?: string;
    containingFunctionDefinition?: import('./Graph').FunctionDefinition;
    allFunctions?: import('./Graph').FunctionDefinition[];
}
export interface ComputeShapeOutput {
    inPorts: NodeInstanceDataPort[];
    outPorts: NodeInstanceDataPort[];
}
export type ComputeShape = (input: ComputeShapeInput) => ComputeShapeOutput;

export interface NodeExecutionResult {
    output: { [key: string]: DataValue };
    executionDecisions?: { [key: string]: boolean };
    // Graph context propagation: contexts produced by this node
    producedContexts?: Record<string, unknown>;
    // API server response (for API response nodes)
    apiResponse?: {
        requestId: string;
        statusCode: number;
        headers?: Record<string, string>;
        body: unknown;
        streaming?: boolean;
        isFinal?: boolean;
    };
}

export interface ExecuteSingleNodeInput {
    inPorts: { [key: string]: DataValue };
    integration?: IntegrationExecutionAccess;
    variableValue?: DataValue;
    setVariableValue: (value: DataValue) => void;
    state: NodeInstanceState;
    payload?: any;
    // Optional function execution support (provided when node has function ports)
    executeFunctionNode?: (functionId: string, inputs: { [key: string]: DataValue }) => Promise<NodeExecutionResult>;
    // Graph context propagation: accumulated contexts from upstream nodes
    contexts?: Record<string, unknown>;
}

export interface SingleNodeBase extends BaseNode {
    computeShape?: ComputeShape;
}

export interface SingleNode extends SingleNodeBase {
    execute: (input: ExecuteSingleNodeInput) => NodeExecutionResult;
    isAsync: false;
}

export interface AsyncSingleNode extends SingleNodeBase {
    execute: (input: ExecuteSingleNodeInput) => Promise<NodeExecutionResult>;
    isAsync: true;
}

export type TriggerType = 'Timer' | 'Webhook' | 'Integration' | 'Form';

export function isNodeWithTrigger(node: Node): boolean {
    return node.triggerType !== undefined;
}

export type Node = SingleNode | AsyncSingleNode;

export function createNode<
    InPorts extends { [key: string]: DataType },
    OutPorts extends { [key: string]: DataType },
    IsAsync extends boolean = false,
    HasExecutionDecisions extends boolean = false,
    IntegrationType extends GlueIntegration<any> | undefined = undefined,
>(configuration: {
    id: string;
    name: string;
    description: string;
    aiDescription?: string;
    category: string;
    color: string;
    findableInLibrary?: boolean;
    inPorts: InPorts;
    optionalInPorts?: (keyof InPorts)[];
    outPorts: OutPorts;
    optionalOutPorts?: (keyof OutPorts)[];
    isAsync?: IsAsync;
    integration?: IntegrationType;
    triggerType?: TriggerType;
    hasExecutionDecisions?: HasExecutionDecisions;
    triggerEventIdentifier?: string;
    execute: (input: {
        inPorts: { [P in keyof InPorts]: any };
        integration: IntegrationType extends undefined ? undefined : IntegrationExecutionAccess;
        variableValue?: DataValue;
        setVariableValue: (value: DataValue) => void;
        state: NodeInstanceState;
        payload: IntegrationType extends undefined ? undefined : any;
        executeFunctionNode?: (functionId: string, inputs: { [key: string]: DataValue }) => Promise<NodeExecutionResult>;
    }) => IsAsync extends true
        ? Promise<{
              output: { [P in keyof OutPorts]: DataValue };
              executionDecisions?: HasExecutionDecisions extends true ? { [P in keyof OutPorts]: boolean } : undefined;
          }>
        : {
              output: { [P in keyof OutPorts]: DataValue };
              executionDecisions?: HasExecutionDecisions extends true ? { [P in keyof OutPorts]: boolean } : undefined;
          };
    computeShape?: (input: {
        preResolvedInputPorts: NodeInstanceDataPort[];
        preResolvedOutputPorts: NodeInstanceDataPort[];
        nodeInPorts: { name: keyof InPorts; type: DataType }[];
        nodeOutPorts: { name: keyof OutPorts; type: DataType }[];
        setVariableType: (variableName: string, type?: DataType) => void;
        selectedVariable?: GlueVariable;
        portValues: { [key: string]: DataValue };
        state?: NodeInstanceState;
        // Function context for function-related nodes
        referencedFunctionId?: string;
        containingFunctionDefinition?: FunctionDefinition;
        allFunctions?: FunctionDefinition[];
    }) => {
        inPorts: NodeInstanceDataPort[];
        outPorts: NodeInstanceDataPort[];
    };
    interfaceComponentId?: string; // New: reference to frontend component
    transientStateKeys?: string[];
    hideInherentInPort?: boolean;
    hideInherentOutPort?: boolean;
    version?: number;
}): SingleNode | AsyncSingleNode {
    return {
        id: configuration.id,
        name: configuration.name,
        description: configuration.description,
        aiDescription: configuration.aiDescription,
        category: configuration.category,
        color: configuration.color,
        findableInLibrary: configuration.findableInLibrary ?? true,
        isAsync: configuration.isAsync ?? false,
        hasExecutionDecisions: configuration.hasExecutionDecisions ?? false,
        version: configuration.version,
        inPorts: Object.entries(configuration.inPorts).map(([name, type]) => {
            const isOptional = configuration.optionalInPorts?.includes(name) ?? false;
            return {
                name,
                type: isOptional ? createOptional(type as DataType) : type,
                optional: isOptional,
            };
        }),
        outPorts: Object.entries(configuration.outPorts).map(([name, type]) => {
            const isOptional = configuration.optionalOutPorts?.includes(name) ?? false;
            return {
                name,
                type: isOptional ? createOptional(type as DataType) : type,
                optional: isOptional,
            };
        }),
        triggerType: configuration.triggerType,
        triggerEventIdentifier: configuration.triggerEventIdentifier,
        integration: configuration.integration,
        hasSubGraph: false,
        // @ts-expect-error
        execute: configuration.execute,
        computeShape: configuration.computeShape,
        interfaceComponentId: configuration.interfaceComponentId,
        transientStateKeys: configuration.transientStateKeys,
        hideInherentInPort: configuration.hideInherentInPort ?? false,
        hideInherentOutPort: configuration.hideInherentOutPort ?? false,
    };
}
